import { BRANDS_CATEGORY_ID } from "@faire/help-center-utils/consts/BRANDS_CATEGORY_ID";
import { RETAILERS_CATEGORY_ID } from "@faire/help-center-utils/consts/RETAILERS_CATEGORY_ID";
import { CustomerTypes } from "@faire/help-center-utils/enums/CustomerType";
import { localeString } from "@faire/help-center-utils/lib/localeString";
import { ITopicPlaceholder } from "@faire/help-center-utils/types/topics";
import { useCustomerType } from "@faire/help-center/hooks/useCustomerType";
import { useArticlesDataFetch } from "@faire/help-center/views/articles/hooks/useArticlesDataFetch";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { pathMatchesRoute } from "@faire/web--source/common/routes/RouteMatcher";
import { useQuery as useZendeskSections } from "@faire/web-api--source/api/zendesk/locale/sections/get-hooks";
import { route as homeRoute } from "@faire/web-api--source/routes/support";
import { route as brandsRoute } from "@faire/web-api--source/routes/support/brands";
import { route as retailersRoute } from "@faire/web-api--source/routes/support/retailers";
import { route as searchPageRoute } from "@faire/web-api--source/routes/support/search";
import topicPage from "@faire/web-api--source/routes/support/topics/topicId";
import { useLocation } from "react-router-dom";

import { localBreadcrumbs } from "../localBreadcrumbs";
import { ILinkData, ITopicsContext } from "../types";

const isBrandTopicCategory = (topic: ITopicPlaceholder) => {
  return topic.category_id === BRANDS_CATEGORY_ID;
};

const BrandLinkData = (strictLocalize: StrictLocalizeFunction) => {
  return {
    name: localBreadcrumbs(strictLocalize).BRAND_GUIDES,
    to: brandsRoute,
  };
};

const RetailerLinkData = (strictLocalize: StrictLocalizeFunction) => {
  return {
    name: localBreadcrumbs(strictLocalize).RETAILER_GUIDES,
    to: retailersRoute,
  };
};

export const usePath = () => {
  const location = useLocation();
  const { pathname } = location;
  const { customerType } = useCustomerType();
  const { article, inHiddenSection, articleSection, isLoading } =
    useArticlesDataFetch();
  const isBasePath = pathMatchesRoute(pathname, homeRoute);
  const isBrandBasePath = pathMatchesRoute(pathname, brandsRoute);
  const isRetailerBasePath = pathMatchesRoute(pathname, retailersRoute);
  const isPopularGuidesPath = pathname.includes("popular-guides") ?? false;
  // pathMatchesRoute won't work here because the search route has a query param
  const isSearchPath = pathname.startsWith(searchPageRoute);

  const isBrandPath = (ctx: ITopicsContext): boolean => {
    const urlContainsBrandsCategory =
      location?.search.includes(`${BRANDS_CATEGORY_ID}`) ||
      location?.pathname.includes(`${BRANDS_CATEGORY_ID}`);
    if (isBrandBasePath || urlContainsBrandsCategory) {
      return true;
    }
    const isBrandArticle = customerType === CustomerTypes.BRAND;
    const isBrandTopic = ctx.topic ? isBrandTopicCategory(ctx.topic) : false;
    return !ctx.topicsLoading && (isBrandArticle || isBrandTopic);
  };
  const getUserTypeLink = (
    ctx: ITopicsContext,
    strictLocalize: StrictLocalizeFunction
  ) => {
    return isBrandPath(ctx)
      ? BrandLinkData(strictLocalize)
      : RetailerLinkData(strictLocalize);
  };
  const { data: sectionsResponse } = useZendeskSections(localeString);
  const currentSection = sectionsResponse?.sections?.filter(
    (section) => section.id === Number(article?.section_id)
  );
  const getBreadcrumbPaths = (
    ctx: ITopicsContext,
    strictLocalize: StrictLocalizeFunction
  ): ILinkData[] => {
    const paths = [
      { name: localBreadcrumbs(strictLocalize).HOME, to: homeRoute },
    ];
    if (isBrandBasePath) {
      paths.push(BrandLinkData(strictLocalize));
    } else if (isRetailerBasePath) {
      paths.push(RetailerLinkData(strictLocalize));
    } else if (isPopularGuidesPath) {
      paths.push(getUserTypeLink(ctx, strictLocalize));
      paths.push({
        name: localBreadcrumbs(strictLocalize).POPULAR_GUIDES,
        to: "",
      });
    } else if (isSearchPath) {
      paths.push(getUserTypeLink(ctx, strictLocalize));
      paths.push({ name: localBreadcrumbs(strictLocalize).SEARCH, to: "" });
    } else if (!ctx.topicsLoading && ctx.topic) {
      paths.push(
        isBrandTopicCategory(ctx.topic)
          ? BrandLinkData(strictLocalize)
          : RetailerLinkData(strictLocalize)
      );
      paths.push({ name: ctx.topic.name ?? "", to: "" });
    } else if (!ctx.topicsLoading && article) {
      paths.push(
        customerType === CustomerTypes.BRAND
          ? BrandLinkData(strictLocalize)
          : RetailerLinkData(strictLocalize)
      );
      if (!inHiddenSection) {
        paths.push({
          name: articleSection?.name ?? currentSection?.[0]?.name ?? "",
          to: topicPage(`${article.section_id}`),
        });
      }
      paths.push({ name: article.name ?? "", to: "" });
    }
    return paths;
  };

  const getCategoryId = (ctx: ITopicsContext) => {
    return isBrandPath(ctx) ? BRANDS_CATEGORY_ID : RETAILERS_CATEGORY_ID;
  };

  return {
    isBasePath,
    isBrandBasePath,
    isRetailerBasePath,
    isPopularGuidesPath,
    isSearchPath,
    isBrandPath,
    getBreadcrumbPaths,
    getCategoryId,
    isLoading,
  };
};
